import React, { Component } from "react";
import { CurrencyUtil, I8N } from "@eres/core";
import Strings from "@my-dld/assets/strings";
import { TenancyRequest } from "@eres/ejari-api/Entity/tenant";
import { EjariApi } from "@eres/ejari-api";
import { TenantRequestStatus, TenantRequestUserType } from "@eres/ejari-core/constants";
import { verifyTenantUserProfile } from "../../../component/TenantContract/PropertyDetails";
import { showErrorMessage } from "@my-dld/utils/UIUtils";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { resetTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { navigate } from "@my-dld/utils/StoreUtils";
import { getEjariPropertyTypeName, getTenantRequestStatus, IDtoEnum } from "@eres/ejari-api/Utils";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import TenantRequestsListView from "../../../component/TenantRequests/TenantRequestsListView";
import TenantRequestsGridView from "../../../component/TenantRequests/TenantRequestsGridView";
import { formatAppDate } from "@my-dld/utils/Utils";

interface Props {

}

interface State {
	isLoading: boolean;
	requests: TenancyRequest[];
}

class MyTenantRequests extends Component<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			requests: [],
		};
	}

	componentDidMount(): void {
		this.verifyTenantUserProfile();
	}

	render() {
		const { isLoading, requests } = this.state;
		return <ListGridDataView
			isLoading={ isLoading }
			dataAvailable={ requests.length > 0 }
			title={ Strings.my_tenancy_requests }
			renderListView={ this.renderListView }
			renderGridView={ this.renderGridView }/>;
	}

	protected showTenantReqDetails( request: TenancyRequest, userTypeId: number ) {
		const contractTypeId = request.ContractTypeId;
		const contractId = request.TenancyContractId;
		trackEventWithData( "TenantRequest", "View", {
			userTypeId: "" + userTypeId, contractTypeId: "" + contractTypeId, contractId: "" + contractId
		} );
		resetTenantContractContext();
		navigate( `/mydld/tenant/request/${ userTypeId }/${ contractTypeId }/${ contractId }` );
	}

	private renderListView = () => {
		const { requests } = this.state;
		return <TenantRequestsListView
			items={ requests }
			onDetails={ ( request: TenancyRequest ) => {
				showTenantReqDetails( request, TenantRequestUserType.TENANT );
			} }/>;
	};

	private renderGridView = () => {
		const { requests } = this.state;
		return <TenantRequestsGridView
			items={ requests }
			onDetails={ ( request: TenancyRequest ) => {
				showTenantReqDetails( request, TenantRequestUserType.TENANT );
			} }/>;
	};

	private verifyTenantUserProfile() {
		verifyTenantUserProfile().then( ( tenantId: number | undefined ) => {
			if ( tenantId && tenantId !== 0 ) {
				this.loadTenancyRequests( tenantId );
			} else {
				this.handleResponse( [] );
				showErrorMessage( Strings.tenant_verify_error );
			}
		} ).catch( e => {
			this.setState( { isLoading: false } );
			showErrorMessage( e );
		} );
	}

	private loadTenancyRequests( tenantId: number ) {
		EjariApi.getTenancyRequests( 0, tenantId, TenantRequestUserType.TENANT )
		.then( ( requests: TenancyRequest[] ) => {
			this.handleResponse( requests );
		} ).catch( reason => {
			this.handleResponse( [] );
		} );
	}

	private handleResponse( requests: TenancyRequest[] ) {
		this.setState( {
			requests: requests,
			isLoading: false,
		} );
	}
}

export function getTenantReqPropertyTypeName( propertyType: number ) {
	return getEjariPropertyTypeName( propertyType );
}

export function getTenantReqBuildingName( request: TenancyRequest ) {
	const name = I8N.isRTL() ? request.BuildingNameAr : request.BuildingNameEn;
	return !isEmptyStringOrNull( name ) ? name : "-";
}

export function getTenantReqUnitNo( request: TenancyRequest, propertyType: number ) {
	const unitNo = ( propertyType === EjariPropertyType.UNIT ) ? request.PropertyNumber : "-";
	return !isEmptyStringOrNull( unitNo ) ? unitNo! : "-";
}

export function getTenantReqStartDate( request: TenancyRequest ) {
	let sd = "";
	if ( request.ContractStartDate )
		sd = formatAppDate( request.ContractStartDate?.asDate() );
	return sd;
}

export function getTenantReqEndDate( request: TenancyRequest ) {
	let ed = "";
	if ( request.ContractEndDate )
		ed = formatAppDate( request.ContractEndDate?.asDate() );
	return ed;
}

export function getTenantReqContractNo( request: TenancyRequest ) {
	if ( request.ContractNumber && request.VersionNumber )
		return `${ request.ContractNumber } (V-${ request.VersionNumber })`;
	return "-";
}

export function getTenantReqStatus( request: TenancyRequest ) {
	const status = IDtoEnum<TenantRequestStatus>( TenantRequestStatus, request.Status )!;
	return getTenantRequestStatus( status );
}

export function getTenantReqAmount( request: TenancyRequest ) {
	let contractAmt: string | number | undefined = request.ContractAmount;
	if ( contractAmt == null || contractAmt <= 0 )
		contractAmt = "";
	else {
		contractAmt = CurrencyUtil.toFormattedCurrency( Number( contractAmt ) );
	}
	return contractAmt;
}

export function showTenantReqDetails( request: TenancyRequest, userTypeId: number ) {
	const contractTypeId = request.ContractTypeId;
	const contractId = request.TenancyContractId;
	trackEventWithData( "TenantRequest", "View", {
		userTypeId: "" + userTypeId, contractTypeId: "" + contractTypeId, contractId: "" + contractId
	} );
	resetTenantContractContext();
	navigate( `/mydld/tenant/request/${ userTypeId }/${ contractTypeId }/${ contractId }` );
}

export default MyTenantRequests;
